import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import { BaseView } from '@bingads-webui/base-view';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { FilterService } from '@bingads-webui/filter-service';
import {
  METRICS,
  getLocalizedMetrics
} from '@bingads-webui-campaign-react/advisor-chart';
import {
  CAMPAIGN_TYPES,
  METRIC_PREFERENCE_KEY_NAME,
  GENERAL_INSIGHTS_FEATURE_NAMES,
} from '@bingads-webui-campaign/competition-auction-insight-preferences';

import {
  fetchPreferenceForGeneralInsights,
  getGeneralInsightsPreferences,
  saveGeneralInsightsPreferences,
  getFilterConfig,
  instrumentationSchema,
} from '@bingads-webui-campaign-react/competition-core';

import { CompetitionTile } from './competition-tile';
import competitionTileTemplate from './competition-tile-template.pug';
import { DomainSelectorView } from './domain-selector';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/dashboard-cards');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const appConfig = globalContext.require('component/config/index');
const appState = globalContext.require('component/state/index');
const dateUtil = globalContext.require('component/humanize/date');
const { fetchSortInfo } = globalContext.require('component/bingads-grid/service/fetch-sortinfo');
const sessionStore = globalContext.requireDefault('component/js-data-storage/session-storage');
const preferenceStoreFactory = globalContext.require('component/bingads-grid/service/filter-preferences').getFilters;
const cacheService = globalContext.require('Services/CacheService');
const dataContextManager = globalContext.require('SummaryDataContextManager');
const edmCampaign = globalContext.requireDefault('component/edm-campaign/index');
const jsdata = globalContext.require('component/js-data-campaign/index');
const preferencesService = globalContext.requireDefault('component/preference/service/index');
const { getSegPreference, getSegType } = globalContext.require('component/bingads-grid/service/segmentation-preferences');

const permissions = appConfig.get('Permissions');

export class CompetitionTileView extends BaseView {
  initialize(options) {
    this.options = options;
    this.state = {
      metric: null,
      chartData: {
        totalCount: 0,
        items: [],
      },
      domains: null,
      chartType: 'SmallTile',
    };

    // should set first as some below codes depending on it.
    this.campaignType = this.options.scope.campaignType;
    this.metricPreferenceKeyName = METRIC_PREFERENCE_KEY_NAME[this.campaignType];

    this.fetchPreferenceToken = this.options.preexecuteFlag ? null :
      createAsyncToken(fetchPreferenceForGeneralInsights({
        campaignType: this.campaignType,
        preferencesService,
        getSegPreference,
        fetchSortInfo,
        appState,
        getSegType,
      }))
        .on({
          onSuccess: () => {
            const preferences = getGeneralInsightsPreferences(preferencesService);
            const availableMetrics = _.map(this.getLocalizedMetrics(), item => item.metric);
            let selectedMetric = preferences[this.metricPreferenceKeyName]
              || METRICS.ImpressionShare;

            if (!_.contains(availableMetrics, selectedMetric)) {
              selectedMetric = METRICS.ImpressionShare;

              _.defer(() => {
                saveGeneralInsightsPreferences({
                  [this.metricPreferenceKeyName]: selectedMetric,
                }, preferencesService);
              });
            }
            this.trigger('initialMetric', selectedMetric);
          },
        });

    this.initializeInstrumentation();
    this.dataService = options.getAuctionInsightDataService(this.options.scope);

    this.chartDataSource = options.getDataSource({
      dataService: this.dataService,
    });

    if (!options.preexecuteFlag) {
      this.refreshDataAsync();
    }

    // initializeChildren
    this.setChild('selector', new DomainSelectorView({
      i18n,
    }));
  }

  initializeInstrumentation() {
    instrumentation.addScenario(instrumentationSchema);
    this.currentScenario = instrumentation.loadGeneralInsights.create();
    this.currentActivity = this.currentScenario.loadGeneralInsights.create();
  }

  saveDomainFilter() {
    // if the user selected any domains, save them as the current filter
    if (!_.isEmpty(this.state.domains)) {
      const filterService = new FilterService(getFilterConfig({
        scope: this.scope,
        appState,
        dateUtil,
        sessionStore,
        preferenceStoreFactory,
        cacheService,
        dataContextManager,
        edmCampaign,
        jsdata,
        i18n,
        permissions,
        featureName: GENERAL_INSIGHTS_FEATURE_NAMES[this.campaignType],
      }));
      filterService.setEditableFilter(
        { DisplayDomain: { $in: this.state.domains } },
        ''
      );
    }
  }

  refreshDataAsync() {
    if (this.options.preexecuteFlag) {
      return;
    }

    this.currentActivity.trace('start refresh chart data', 'CompetitionTile/refreshData');
    const refreshChartActivity = this.currentScenario.refreshChart.create();

    refreshChartActivity.start();
    this.trigger('willFetchData');
    this.fetchError = false;
    this.refreshDataToken = createAsyncToken(this.chartDataSource.query())
      .on({
        onSuccess: (chartData) => {
          this.currentActivity.trace('end refresh chart data', 'CompetitionTile/refreshData');
          refreshChartActivity.stop();
          this.trigger('initialData', _.result(chartData, 'totalCount', 0));
          _.each(chartData.items, (item) => {
            if (item.DisplayDomain === 'You') {
              item.LocalizedDisplayDomain = i18n.getString('AuctionInsights_You'); // eslint-disable-line
            }
          });
          this.updateState({ chartData });
          this.options.clearCache();
        },
        onError: () => {
          this.fetchError = true;
          this.render();
          this.trigger('fetchError');
          this.options.clearCache();
        },
        onCancel: () => {
          this.options.clearCache();
        },
      });
  }

  getLocalizedMetrics() {
    return getLocalizedMetrics(
      i18n, this.isShoppingCampaignTypeSet(),
      permissions.IsAvgPositionDeprecationPhase1Enabled,
      permissions.IsAvgPositionDeprecationPhase2Enabled
    );
  }

  isShoppingCampaignTypeSet() {
    return this.campaignType === CAMPAIGN_TYPES.Shopping;
  }

  render() {
    this.$el.html(competitionTileTemplate({ i18n, fetchError: this.fetchError }));

    this.$('.domain-selector').html(this.getChild('selector').render().$el);
    this.getChild('selector').on('selectionChanged', (domains) => {
      this.updateState({ domains });
    });

    this.updateChart();
    this.updateDomainSelector();

    return this;
  }

  updateState({
    metric = null,
    chartData = null,
    domains = null,
    chartType = null,
  }) {
    if (metric) {
      if (this.state.metric && this.state.metric !== metric) {
        saveGeneralInsightsPreferences({
          [this.metricPreferenceKeyName]: metric,
        }, preferencesService);
      }
      this.state.metric = metric;
    }

    if (chartData) {
      this.state.chartData = chartData;
    }

    if (domains) {
      this.state.domains = domains;
    }

    if (chartType) {
      this.state.chartType = chartType;
    }

    // rerender components
    this.updateChart();

    if ((chartData || metric) && this.state.chartData && this.state.metric) {
      this.updateDomainSelector();
    }
  }

  updateDomainSelector() {
    // Don't update if metric is null.
    if (this.state.metric !== null) {
      this.getChild('selector').updateData(this.state.chartData);
    }
  }

  updateChart() {
    // Don't update if metric is null.
    if (this.state.metric !== null) {
      ReactDOM.render(
        (
          <CompetitionTile
            metric={this.state.metric}
            chartData={this.state.chartData}
            domains={this.state.domains}
            chartType={this.state.chartType}
          />
        ), this.$('.graph')[0]
      );
    }
  }

  remove() {
    if (this.refreshDataToken) this.refreshDataToken.cancel();
    if (this.fetchPreferenceToken) this.fetchPreferenceToken.cancel();
    ReactDOM.unmountComponentAtNode(this.$('.graph')[0]);
    return super.remove();
  }
}
